import { barcodesMatch } from '../../utils/barcode';

// Pure helpers for the order picking screens (list + detail), kept out of the
// components so they can be unit tested.

export const toMillis = (timestamp) => (timestamp?.seconds ? timestamp.seconds * 1000 : 0);

const dueTime = (order) => toMillis(order.dispatchDate) || toMillis(order.createTime);

export const orderPath = (orderId) => `/orders/${encodeURIComponent(orderId)}`;

// ---------- Order list ----------

// Search (order ID or customer) and customer filter. Null customer names count as
// 'Unknown' on both sides so the "Unknown" dropdown option matches them.
export const filterOrders = (orders, { searchTerm = '', customerFilter = '' } = {}) => {
  const term = searchTerm.trim().toLowerCase();
  return orders.filter(order => {
    if (term && !(
      (order.id && order.id.toLowerCase().includes(term)) ||
      (order.customerName && order.customerName.toLowerCase().includes(term))
    )) return false;
    if (customerFilter && (order.customerName || 'Unknown') !== customerFilter) return false;
    return true;
  });
};

// Ready view: most urgent dispatch date first. Completed view: most recently
// completed first (dispatch date breaks ties / covers rows with no completion time).
export const sortOrders = (orders, showCompleted) => {
  const result = [...orders];
  if (showCompleted) {
    result.sort((a, b) =>
      (toMillis(b.pickCompleteTime) - toMillis(a.pickCompleteTime)) || (dueTime(b) - dueTime(a))
    );
  } else {
    result.sort((a, b) => dueTime(a) - dueTime(b));
  }
  return result;
};

const isFullyPicked = (order) => {
  const total = order.totalItems || 0;
  return total > 0 && (order.pickedCount || 0) >= total;
};

// The order a picker should do next: most urgent first, skipping `excludeId` and
// orders whose lines are all picked (they only need confirming). Orders nobody has
// started ('Ready to Pick') win over 'In Progress' ones, which someone else is
// probably picking.
export const findNextOrder = (orders, excludeId = null) => {
  const candidates = sortOrders(orders, false).filter(order =>
    order.id !== excludeId && order.status !== 'Picked Complete' && !isFullyPicked(order)
  );
  return candidates.find(order => (order.status || 'Ready to Pick') === 'Ready to Pick')
    || candidates[0]
    || null;
};

// A scanned code that is an order number (case and surrounding spaces ignored)
export const findOrderByCode = (orders, code) => {
  const wanted = String(code || '').trim().toUpperCase();
  if (!wanted) return null;
  return orders.find(order => String(order.id || '').trim().toUpperCase() === wanted) || null;
};

// Status pill text + class. Fully picked but unconfirmed orders get their own label.
export const orderStatusInfo = (order) => {
  if (isFullyPicked(order) && order.status !== 'Picked Complete') {
    return { label: 'Awaiting Confirmation', className: 'status-awaiting-confirmation' };
  }
  const label = order.status || 'Ready to Pick';
  return { label, className: `status-${label.toLowerCase().replace(/\s+/g, '-')}` };
};

// ---------- Order detail ----------

export const isInsufficientStock = (item) => {
  if (item.warehouseBalance === undefined || item.warehouseBalance === null || item.warehouseBalance === 'N/A') {
    return false;
  }
  const balance = Number(item.warehouseBalance);
  const required = Number(item.quantity);
  return !isNaN(balance) && !isNaN(required) && balance < required;
};

// Units scanned so far on a line (a picked line counts as its full quantity)
export const unitsPicked = (item) => {
  const qty = Number(item.quantity) || 0;
  if (item.picked) return qty;
  return Math.min(Number(item.scanCount) || 0, qty);
};

// "Items" is ambiguous: lines are order rows, units are the frames on them.
export const summarizePick = (items) => items.reduce((sum, item) => ({
  linesPicked: sum.linesPicked + (item.picked ? 1 : 0),
  linesTotal: sum.linesTotal + 1,
  unitsPicked: sum.unitsPicked + unitsPicked(item),
  unitsTotal: sum.unitsTotal + (Number(item.quantity) || 0),
}), { linesPicked: 0, linesTotal: 0, unitsPicked: 0, unitsTotal: 0 });

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export const formatPickSummary = ({ linesPicked, linesTotal, unitsPicked: units, unitsTotal }) =>
  `${linesPicked} of ${plural(linesTotal, 'line')} · ${units} of ${plural(unitsTotal, 'unit')} picked`;

// Lines still to pick first, picked lines after (stable, so Priority line order is
// kept within each group). Ids in `holdIds` were just picked and keep their place
// for a moment so the scan highlight is seen before the row moves.
export const orderItemsForDisplay = (items, holdIds = new Set()) => {
  const sinks = (item) => item.picked && !holdIds.has(item.id);
  return [
    ...items.filter(item => !sinks(item)),
    ...items.filter(sinks),
  ];
};

// What a scanned barcode means for this order. `locallyPicked` holds ids this client
// has just picked but realtime hasn't confirmed yet. Unpicked lines are preferred so
// duplicate lines (same barcode, different qty) fill one after the other.
//   { kind: 'notFound' } | { kind: 'alreadyPicked', item } | { kind: 'pick', item }
// After `itemId` has just been fully picked: is every line on the order now picked?
export const isOrderCompleteAfter = (items, itemId, locallyPicked = new Set()) =>
  items.length > 0 && items.every(item => item.id === itemId || item.picked || locallyPicked.has(item.id));

export const resolveScan = (items, barcode, locallyPicked = new Set()) => {
  const isPicked = (item) => item.picked || locallyPicked.has(item.id);
  const matches = items.filter(item => barcodesMatch(barcode, item.barcode));
  if (matches.length === 0) return { kind: 'notFound' };
  const open = matches.find(item => !isPicked(item));
  if (open) return { kind: 'pick', item: open };
  return { kind: 'alreadyPicked', item: matches[0] };
};

// Priority prints the same confirmation footer on every sales order. It tells the
// picker nothing, so drop it and keep only notes someone actually wrote.
const BOILERPLATE_NOTES = [
  /please check the above information is correct and notify us of any amendments within 24 hours\.?/gi,
  /this is a sales order confirmation\s*-\s*not an invoice\s*\.?/gi,
];

export const pickNotes = (notes) => {
  if (!notes) return '';
  const text = BOILERPLATE_NOTES.reduce((t, re) => t.replace(re, ' '), String(notes));
  return text.replace(/\s+/g, ' ').replace(/^[\s.,;:-]+|[\s,;:-]+$/g, '').trim();
};
